"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { getProductPricing, ProductVariant, StoreProduct } from "../utils/product";

export interface CartItem {
  product: StoreProduct;
  variant?: ProductVariant;
  quantity: number;
}

export const getCartItemKey = (item: Pick<CartItem, "product" | "variant">) => `${item.product.id}:${item.variant?.id || "default"}`;

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (product: StoreProduct, quantity?: number, variant?: ProductVariant) => void;
  removeFromCart: (itemKey: string) => void;
  updateQuantity: (itemKey: string, quantity: number) => void;
  clearCart: () => void;
  subtotal: number;
  itemCount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load cart on component mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedCart = localStorage.getItem("torongo_cart");
      if (storedCart && storedCart !== "undefined") {
        try {
          setCartItems(JSON.parse(storedCart));
        } catch {
          localStorage.removeItem("torongo_cart");
        }
      } else {
        localStorage.removeItem("torongo_cart");
      }
      setIsInitialized(true);
    }
  }, []);

  // Save cart to local storage whenever it changes
  useEffect(() => {
    if (isInitialized && typeof window !== "undefined") {
      localStorage.setItem("torongo_cart", JSON.stringify(cartItems));
    }
  }, [cartItems, isInitialized]);

  const addToCart = (product: StoreProduct, quantity = 1, variant?: ProductVariant) => {
    setCartItems((prevItems) => {
      const itemKey = getCartItemKey({ product, variant });
      const existingItemIndex = prevItems.findIndex((item) => getCartItemKey(item) === itemKey);
      const availableStock = variant?.stock ?? product.stock;
      const cartProduct = { ...product, price: getProductPricing(product).payablePrice };

      if (existingItemIndex > -1) {
        const updatedItems = [...prevItems];
        const newQty = updatedItems[existingItemIndex].quantity + quantity;
        // Limit quantity to stock level
        updatedItems[existingItemIndex].quantity = Math.min(newQty, availableStock);
        return updatedItems;
      } else {
        return [...prevItems, { product: cartProduct, variant, quantity: Math.min(quantity, availableStock) }];
      }
    });
  };

  const removeFromCart = (itemKey: string) => {
    setCartItems((prevItems) => prevItems.filter((item) => getCartItemKey(item) !== itemKey));
  };

  const updateQuantity = (itemKey: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemKey);
      return;
    }
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        getCartItemKey(item) === itemKey
          ? { ...item, quantity: Math.min(quantity, item.variant?.stock ?? item.product.stock) }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const subtotal = cartItems.reduce((acc, item) => acc + item.product.price * item.quantity, 0);
  const itemCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        subtotal,
        itemCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
